/**
 * Pure positioning helper for the profile popover.
 *
 * The panel is teleported (it lives outside the trigger's normal flow) and is
 * absolutely positioned so it can spill over neighboring content. The trigger
 * sits in the top-right of the header, so on narrow viewports (320–375px) the
 * trigger's right edge leaves very little room for a `w-72` (288px) panel. The
 * naive anchor — `right: viewport - trigger.right` — overflows the left edge
 * of the viewport, hiding the trigger's avatar / username area on small
 * phones.
 *
 * `clampPanelPosition` keeps the trigger's right-edge alignment when the panel
 * has room to breathe, and falls back to a left-aligned clamp inside the
 * safe margin when it doesn't. The function is intentionally pure: it takes
 * measured rects and returns pixel coordinates, so it can be unit-tested
 * without a DOM.
 *
 * Mirroring the existing `$space-2` (0.5rem) token means the safe margin stays
 * in lock-step with the rest of the app's spacing system rather than
 * introducing a one-off magic number.
 */

export interface TriggerRect {
  /** Viewport-relative right edge of the trigger button. */
  readonly right: number
  /** Viewport-relative bottom edge of the trigger button. */
  readonly bottom: number
}

export interface PanelRect {
  /** Measured panel width in CSS pixels. */
  readonly width: number
  /** Measured panel height in CSS pixels. */
  readonly height: number
}

export interface ViewportRect {
  /** Viewport width in CSS pixels. */
  readonly width: number
}

export interface PanelPosition {
  /** Viewport-relative left edge of the panel in CSS pixels. */
  readonly left: number
  /** Viewport-relative top edge of the panel in CSS pixels. */
  readonly top: number
}

/**
 * Tolerance for floating-point comparisons. When the trigger's right edge
 * sits exactly at `viewport.width - safeMargin`, the panel just barely fits
 * anchored to the right. Treating values within this tolerance as "fits
 * exactly" avoids floating-point-induced off-by-one rounding that would push
 * the panel an unnecessary pixel to the left.
 */
const FLOAT_TOLERANCE = 0.5

/**
 * Compute the panel's viewport-relative top-left, given the measured sizes of
 * the trigger, the panel, and the viewport. The panel is anchored to the
 * trigger's bottom-right corner when it fits inside `[margin, viewport -
 * width - margin]`; otherwise it is clamped to the safe margin on the side
 * that overflows.
 *
 * The returned `top` is the trigger's bottom edge (the popover sits flush
 * below the trigger). Vertical clamping is not performed here — the panel
 * scrolls if it ever exceeds the viewport height, and the profile panel
 * is short enough that overflow is not a practical concern.
 *
 * @param trigger  Measured trigger bounding rect (`right`, `bottom`).
 * @param panel    Measured panel bounding rect (`width`, `height`).
 * @param viewport Measured viewport width.
 * @param safeMargin Desired horizontal distance from the viewport edges
 *   (matches the `$space-2` token = 0.5rem / 8px by convention).
 */
export function clampPanelPosition(
  trigger: TriggerRect,
  panel: PanelRect,
  viewport: ViewportRect,
  safeMargin: number,
): PanelPosition {
  const panelRight = trigger.right
  const panelLeft = panelRight - panel.width
  const minLeft = safeMargin
  const maxLeft = Math.max(safeMargin, viewport.width - panel.width - safeMargin)

  // Right-anchor when the panel fits; otherwise clamp to the nearest side.
  const left =
    panelLeft >= minLeft - FLOAT_TOLERANCE
      ? Math.min(panelLeft, maxLeft)
      : minLeft

  return { left, top: trigger.bottom }
}
